<script setup>
import { ref, watch } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import Switch from 'next/switch/Switch.vue';

// 前端 UA 关键词过滤(在生成的 widget 脚本里, 打开页面时检查 navigator.userAgent)。
// 拦截词存在 account.settings.bot_filter_list, 可在此增删, 即时生效(下次页面加载的脚本)。
// 与后端 web_widget.rb 的 DEFAULT_BOT_UA_PATTERNS 保持一致(为空时的兜底)。
const DEFAULT_PATTERNS = [
  'googlebot', 'bingbot', 'baiduspider', 'sogou', '360spider', 'yandexbot',
  'duckduckbot', 'slurp', 'petalbot', 'bytespider', 'applebot',
  'semrushbot', 'ahrefsbot', 'mj12bot', 'dotbot',
  'facebookexternalhit', 'twitterbot', 'linkedinbot', 'pinterestbot',
  'telegrambot', 'discordbot',
  'spider', 'crawl', 'indexer', 'archiver', 'fetcher', 'scanner',
  'python-requests', 'python-urllib', 'curl', 'wget', 'go-http-client',
  'okhttp', 'node-fetch', 'axios', 'scrapy', 'httpx', 'httpclient',
  'headlesschrome', 'phantomjs', 'puppeteer', 'playwright',
  'selenium', 'webdriver', 'chrome-lighthouse',
];

const { currentAccount, updateAccount } = useAccount();

const isEnabled = ref(true);
const patterns = ref([...DEFAULT_PATTERNS]);
const newPattern = ref('');

const syncFromAccount = () => {
  const s = currentAccount.value?.settings || {};
  isEnabled.value = s.bot_filter_enabled !== false;
  const list = s.bot_filter_list;
  patterns.value = Array.isArray(list) && list.length ? [...list] : [...DEFAULT_PATTERNS];
};

watch(currentAccount, syncFromAccount, { deep: true, immediate: true });

const persistList = async list => {
  try {
    await updateAccount({ bot_filter_list: list });
  } catch (error) {
    useAlert('保存失败');
  }
};

const toggleBotFilter = async () => {
  try {
    await updateAccount({ bot_filter_enabled: isEnabled.value });
    useAlert(`机器人过滤已${isEnabled.value ? '开启' : '关闭'}`);
  } catch (error) {
    useAlert('更新失败');
  }
};

const addPattern = async () => {
  const v = newPattern.value.trim();
  if (!v) return;
  if (patterns.value.some(p => p.toLowerCase() === v.toLowerCase())) {
    newPattern.value = '';
    return;
  }
  patterns.value = [...patterns.value, v];
  newPattern.value = '';
  await persistList(patterns.value);
  useAlert(`已添加拦截词:${v}`);
};

const removePattern = async idx => {
  const removed = patterns.value[idx];
  patterns.value = patterns.value.filter((_, i) => i !== idx);
  await persistList(patterns.value);
  useAlert(`已移除拦截词:${removed}`);
};

const onInputKeyup = e => {
  if (e.key === 'Enter') addPattern();
};
</script>

<template>
  <div class="p-4 rounded-lg border border-n-slate-4 bg-n-surface-1">
    <!-- 顶部: 标题 + 开关 -->
    <div class="flex items-start justify-between gap-4">
      <div>
        <h3 class="text-sm font-semibold text-n-slate-12">机器人 / 爬虫过滤</h3>
        <p class="text-xs text-n-slate-11 mt-1">
          打开网页自动建会话时, 按浏览器 UA 关键词挡掉爬虫 / 机器人, 只给真访客建会话。默认开启。
        </p>
      </div>
      <Switch v-model="isEnabled" @change="toggleBotFilter" />
    </div>

    <!-- 拦截词编辑 -->
    <div v-if="isEnabled" class="mt-4">
      <div class="text-xs font-medium text-n-slate-11 mb-2">
        拦截词(访客 UA 含任一子串即拦, 不区分大小写)
      </div>
      <div class="flex flex-wrap gap-1.5 mb-2 max-h-32 overflow-y-auto">
        <span
          v-for="(p, idx) in patterns"
          :key="p + idx"
          class="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-n-slate-3 dark:bg-n-solid-2 text-n-slate-11 font-mono"
        >
          {{ p }}
          <button
            type="button"
            class="text-n-slate-9 hover:text-n-ruby-9"
            title="移除"
            @click="removePattern(idx)"
          >
            ×
          </button>
        </span>
        <span v-if="!patterns.length" class="text-xs text-n-slate-9">(空, 不拦截任何 UA)</span>
      </div>
      <div class="flex items-center gap-2">
        <input
          v-model="newPattern"
          type="text"
          placeholder="新增拦截词, 如 bytespider / gptbot ..."
          class="flex-1 h-8 px-2 text-xs rounded-md border border-n-weak bg-n-input-background text-n-slate-12 outline-none focus:border-n-brand"
          @keyup="onInputKeyup"
        />
        <button
          type="button"
          class="h-8 px-3 text-xs rounded-md bg-n-brand text-white hover:opacity-90"
          @click="addPattern"
        >
          添加
        </button>
      </div>

      <div class="mt-3 p-2.5 rounded text-xs text-n-slate-11 bg-n-amber-9/10 border border-n-amber-9/20">
        <b>说明:</b> 此过滤只挡「打开页面自动建会话」。真访客(含被误判的)只要
        <b>真发一条消息</b>, 仍正常建会话并分配客服、AI 照常回复, 不受影响。
        改完即时生效(下个访客加载页面时拿到的脚本即含新规则)。
      </div>
    </div>
  </div>
</template>
